import { pick } from 'lodash';
import type { Poll, Question } from 'schema/poll';
import type { AnswerState, ICState } from 'schema/state';
import { getQuestionGroupId, selectDisplayedOrAllGroupQuestions, selectGroup, selectQuestion } from '../../selects';
import { removeCyclesFromAnswersStack, removeGroupsFromAnswersStack } from '../../utils';
import { createDefaultAnswerState } from '../creator';

function selectQuestions(
  poll: Poll,
  answersStack: ICState['answersStack'],
): { previousQuestion: Question | null; currentQuestion: Question | null } {
  const answersStackWithoutGroups = removeGroupsFromAnswersStack(poll, answersStack, { withSequentialDisplay: true });
  const answersStackWithoutCycles = removeCyclesFromAnswersStack(answersStackWithoutGroups);

  const { id: previousQuestionId } = answersStackWithoutCycles[answersStackWithoutCycles.length - 2] ?? {};
  const { id: currentQuestionId } = answersStackWithoutCycles[answersStackWithoutCycles.length - 1] ?? {};

  if (!previousQuestionId || !currentQuestionId) {
    return { previousQuestion: null, currentQuestion: null };
  }

  const previousQuestion = selectQuestion(poll, previousQuestionId);
  const currentQuestion = selectQuestion(poll, currentQuestionId);

  return { previousQuestion, currentQuestion };
}

export function back(poll: Poll, prevState: ICState): ICState {
  const { answersStack, backAnswerStack } = prevState;
  const { previousQuestion, currentQuestion } = selectQuestions(poll, answersStack);

  if (!previousQuestion || !currentQuestion) return prevState;

  const newAnswersStack = [...answersStack];

  const currentQuestionGroupId = getQuestionGroupId(currentQuestion, poll.questionDecks);
  const currentGroup = currentQuestionGroupId ? selectGroup(poll, currentQuestionGroupId) : null;
  /** Удаляем из answerStack вопросы с текущей страницы */
  if (currentGroup?.id && !currentGroup.data.hasSequentialDisplay) {
    const groupQuestions = selectDisplayedOrAllGroupQuestions(poll, currentGroup.id, prevState.answersStack);

    for (let i = newAnswersStack.length - 1; i >= 0; i--) {
      if (!groupQuestions.find(question => question.id === newAnswersStack[i]?.id)) {
        break;
      }
      const previousAnswerState = newAnswersStack.pop();
      if (previousAnswerState) {
        backAnswerStack.push({
          ...pick(previousAnswerState, [
            'id',
            'lastTouchedBy',
            'createdAt',
            'answeredIds',
            'answersIds',
            'answeredOthers',
            'answersOrder',
          ]),
          isUsed: false,
        });
      }
    }
  } else {
    const previousAnswerState = newAnswersStack.pop();
    if (previousAnswerState) {
      backAnswerStack.push({
        ...pick(previousAnswerState, [
          'id',
          'lastTouchedBy',
          'createdAt',
          'answeredIds',
          'answersIds',
          'answeredOthers',
          'answersOrder',
        ]),
        isUsed: false,
      });
    }
  }

  const previousQuestionGroupId = getQuestionGroupId(previousQuestion, poll.questionDecks);
  const previousGroup = previousQuestionGroupId ? selectGroup(poll, previousQuestionGroupId) : null;
  /** Сбрасываем в answerStack вопросы с предыдущей страницы */
  if (previousGroup?.id && !previousGroup.data.hasSequentialDisplay) {
    const groupQuestions = selectDisplayedOrAllGroupQuestions(poll, previousGroup.id, prevState.answersStack);
    for (let i = newAnswersStack.length - 1; i >= 0; i--) {
      if (!groupQuestions.find(question => question.id === newAnswersStack[i]?.id)) {
        break;
      }

      resetPreviousAnswerState(poll, prevState, newAnswersStack, i);
    }
  } else {
    /** Вопрос с предыдущей страницы будет лежать последним в newAnswersStack */
    resetPreviousAnswerState(poll, prevState, newAnswersStack, newAnswersStack.length - 1);
  }

  return {
    ...prevState,
    ...(!!previousQuestion.id && { groupId: null, questionId: previousQuestion.id }),
    ...(!!previousQuestionGroupId && { groupId: previousQuestionGroupId, questionId: null }),
    answersStack: newAnswersStack,
  };
}

/** Метод сбрасывает answerState, оставляя ответы и порядок */
function resetPreviousAnswerState(poll: Poll, prevState: ICState, answersStack: AnswerState[], index: number) {
  const previousAnswerState = answersStack[index];
  if (previousAnswerState) {
    const { defaultAnswerState } = createDefaultAnswerState(
      poll,
      //Т.к это возврат на предыдущий экран, то предзаполнять ответы из backAnswerStack не нужно
      { ...prevState, backAnswerStack: [] },
      previousAnswerState.id,
    );
    answersStack[index] = {
      ...defaultAnswerState,
      ...pick(previousAnswerState, ['createdAt', 'answeredIds', 'answeredOthers', 'answersOrder']),
      lastTouchedBy: null,
    };
  }
}
